import { ArrowRight, Shield, CheckCircle, TrendingUp, DollarSign, Lock, BarChart3 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import SEO from '../SEO';
import { pageSEO, faqSchemas } from '../../seoData';
import RelatedServices from '../RelatedServices';

const CorporateBonds = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);

  const faqs = [
    { question: "What are corporate bonds?", answer: "Corporate bonds are debt instruments issued by companies to raise capital. When you invest in a bond, you're essentially lending money to the company in return for regular interest payments (coupons) and principal repayment at maturity. They offer higher returns than bank FDs with moderate risk." },
    { question: "Are corporate bonds safe?", answer: "Safety depends on the credit rating. We only recommend AAA and AA+ rated bonds from reputable companies, which have very low default risk. We carefully evaluate issuer credentials, financial health, and rating agency assessments before recommending any bond." },
    { question: "What is the minimum investment in corporate bonds?", answer: "Minimum investment is typically Rs.5 Lakhs for corporate bonds. Some bonds may have higher minimums. We help you access quality bond offerings that match your investment amount and return expectations." },
    { question: "How is bond income taxed?", answer: "Interest income from corporate bonds is taxed as per your income tax slab. Capital gains on bonds held over 12 months are taxed at 10% without indexation or 20% with indexation. Listed bonds on exchanges may have different tax treatment. We help optimize your bond portfolio for tax efficiency." },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO {...pageSEO.corporateBonds} faqJsonLd={faqSchemas.corporateBonds} />

      <section className="relative bg-gradient-to-br from-amber-50 via-white to-orange-50 pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <span className="inline-block bg-amber-600 text-white px-4 py-2 rounded-full text-sm font-semibold tracking-wide mb-6">
              FIXED INCOME INVESTMENTS
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Corporate Bonds
              <span className="block bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                High-Yield Fixed Income
              </span>
            </h1>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed max-w-3xl">
              Invest in high-quality corporate bonds for stable, predictable income. We curate bonds 
              from AAA/AA+ rated companies offering competitive yields higher than traditional bank fixed 
              deposits. With expert credit analysis and diversified bond selection, we help you build a 
              robust fixed-income portfolio that delivers regular income with capital safety.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="bg-gradient-to-r from-amber-600 to-orange-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-amber-700 hover:to-orange-700 transition-all transform hover:scale-105 inline-flex items-center justify-center shadow-xl" data-testid="bonds-cta">
                Explore Bonds <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link to="/services" className="border-2 border-amber-600 text-amber-600 hover:bg-amber-600 hover:text-white px-8 py-4 rounded-xl font-bold text-lg transition-all inline-flex items-center justify-center">
                View All Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            <div><div className="text-3xl font-bold text-amber-600 mb-1">AAA/AA+</div><div className="text-gray-600 text-sm">Rated Bonds Only</div></div>
            <div><div className="text-3xl font-bold text-amber-600 mb-1">8-10%</div><div className="text-gray-600 text-sm">Typical Yields</div></div>
            <div><div className="text-3xl font-bold text-amber-600 mb-1">Rs.5L</div><div className="text-gray-600 text-sm">Min Investment</div></div>
            <div><div className="text-3xl font-bold text-amber-600 mb-1">Regular</div><div className="text-gray-600 text-sm">Interest Payouts</div></div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-12 text-center">Why Invest in Corporate Bonds?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: <TrendingUp className="h-7 w-7 text-white" />, title: "Higher Returns than FDs", desc: "Corporate bonds typically offer 1-3% higher returns compared to bank fixed deposits, making them attractive for income-seeking investors." },
              { icon: <Shield className="h-7 w-7 text-white" />, title: "Capital Safety", desc: "AAA/AA+ rated bonds from blue-chip companies have extremely low default risk. We perform rigorous credit analysis before recommending any bond." },
              { icon: <DollarSign className="h-7 w-7 text-white" />, title: "Regular Income", desc: "Bonds pay periodic interest (coupons) - monthly, quarterly, or semi-annually. Ideal for retirees and investors seeking predictable cash flow." },
              { icon: <Lock className="h-7 w-7 text-white" />, title: "Capital Preservation", desc: "Unlike equities, bonds return your principal at maturity. They provide stability and act as a cushion during market downturns in your portfolio." },
              { icon: <BarChart3 className="h-7 w-7 text-white" />, title: "Portfolio Diversification", desc: "Adding bonds to an equity-heavy portfolio reduces overall risk. We recommend 20-40% bond allocation based on your age and risk profile." },
              { icon: <CheckCircle className="h-7 w-7 text-white" />, title: "Expert Selection", desc: "We evaluate credit ratings, issuer financials, yield curves, and market conditions to select the best bonds for your portfolio." },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
                <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center mb-5">{item.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-10 text-center">Corporate Bond FAQs</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-gray-50 rounded-xl overflow-hidden">
                <button onClick={() => setExpandedFaq(expandedFaq === i ? null : i)} className="w-full px-6 py-5 text-left flex justify-between items-center">
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <span className="text-2xl text-amber-600 ml-4">{expandedFaq === i ? '−' : '+'}</span>
                </button>
                {expandedFaq === i && <div className="px-6 pb-5"><p className="text-gray-700 leading-relaxed">{faq.answer}</p></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <RelatedServices links={['gold', 'fd', 'pms', 'retirement', 'calculator']} />

      <section className="py-16 bg-gradient-to-r from-amber-600 to-orange-600">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Start Earning Higher Fixed Income</h2>
          <p className="text-lg mb-8 text-amber-100">Get access to premium corporate bonds with expert guidance. Free consultation.</p>
          <Link to="/contact" className="bg-white text-amber-700 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all inline-flex items-center">
            Explore Bond Options <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CorporateBonds;
